import React, { useState, useEffect, useCallback } from "react";
import {
 Row, Col, Card, Button, Form, Table, Badge, Spinner, ProgressBar,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import {
 createChecklist, updateChecklist, getChecklist,
 getChecklistTemplate, downloadChecklistExcel,
} from "../../../services/safetyChecklistService";

/* Compliance status options */
const STATUS_OPTS = ["Pending", "Compliant", "Non-Compliant", "Partial", "N/A"];

const STATUS_BADGE = {
 Compliant: { bg: "success", label: " Compliant" },
 "Non-Compliant": { bg: "danger", label: " Non-Compliant" },
 Partial: { bg: "warning", label: " Partial" },
 "N/A": { bg: "secondary", label: "N/A" },
 Pending: { bg: "light", label: "—" },
};

const OVERALL_COLORS = { Safe: "success", "At Risk": "warning", Unsafe: "danger", Pending: "secondary" };

/* Compute live score from sections */
const computeScore = (sections) => {
 let total = 0, compliant = 0, nonCompliant = 0, partial = 0, na = 0;
 for (const sec of sections) {
 for (const item of sec.items) {
 total++;
 if (item.status === "Compliant") compliant++;
 else if (item.status === "Non-Compliant") nonCompliant++;
 else if (item.status === "Partial") partial++;
 else if (item.status === "N/A") na++;
 }
 }
 const assessed = total - na;
 const score = assessed > 0 ? Math.round(((compliant + partial * 0.5) / assessed) * 100) : 0;
 let overallStatus = "Pending";
 if (score >= 85) overallStatus = "Safe";
 else if (score >= 60) overallStatus = "At Risk";
 else if (assessed > 0) overallStatus = "Unsafe";
 return { total, compliant, nonCompliant, partial, na, score, overallStatus };
};

/* 
 COMPONENT
 */
const SafetyChecklistForm = ({ mode }) => {
 const navigate = useNavigate();
 const { id } = useParams();
 const isEdit = mode === "edit";

 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [users, setUsers] = useState([]);

 const [meta, setMeta] = useState({
 project: "",
 workOrder: "",
 contractNo: "",
 clientPmc: "",
 inspectionType: "Weekly",
 inspectionDate: new Date().toISOString().slice(0, 10),
 nextInspectionDue: "",
 inspectedBy: "",
 reviewedBy: "",
 status: "Draft",
 remarks: "",
 });

 const [sections, setSections] = useState([]);
 const [loading, setLoading] = useState(false);
 const [fetching, setFetching] = useState(false);
 const [downloading, setDownloading] = useState(false);

 /* live score */
 const score = computeScore(sections);

 /* Load refs */
 useEffect(() => {
 (async () => {
 try {
 const [pRes, uRes] = await Promise.all([
 api.get("/projects?limit=500"),
 api.get("/users?limit=500"),
 ]);
 setProjects(pRes.data?.data || []);
 setUsers(uRes.data?.data || []);
 } catch (_) {}
 })();
 }, []);

 useEffect(() => {
    const params = meta.project ? { project: meta.project, limit: 1000 } : { limit: 1000 };
    api.get("/work-orders", { params })
      .then(r => setWorkOrders(r.data?.data || []))
      .catch(() => {});
  }, [meta.project]);

 /* Seed blank template (new mode) */
 const loadTemplate = useCallback(async () => {
 try {
 const res = await getChecklistTemplate();
 if (res.data?.success) {
 setSections(res.data.data.map(sec => ({
 key: sec.key,
 title: sec.title,
 items: sec.items.map(it => ({
 ...it,
 status: "Pending",
 remarks: "",
 ncrRaised: false,
 })),
 })));
 }
 } catch (_) {}
 }, []);

 useEffect(() => {
 if (!isEdit) loadTemplate();
 }, [isEdit, loadTemplate]);

 /* Load existing checklist (edit mode) */
 useEffect(() => {
 if (!isEdit || !id) return;
 setFetching(true);
 getChecklist(id)
 .then(res => {
 const d = res.data?.data;
 if (!d) return;
 setMeta({
 project: d.project?._id || d.project || "",
 workOrder: d.workOrder?._id || d.workOrder || "",
 contractNo: d.contractNo || "",
 clientPmc: d.clientPmc || "",
 inspectionType: d.inspectionType || "Weekly",
 inspectionDate: d.inspectionDate ? d.inspectionDate.slice(0, 10) : "",
 nextInspectionDue: d.nextInspectionDue ? d.nextInspectionDue.slice(0, 10) : "",
 inspectedBy: d.inspectedBy?._id || d.inspectedBy || "",
 reviewedBy: d.reviewedBy?._id || d.reviewedBy || "",
 status: d.status || "Draft",
 remarks: d.remarks || "",
 });
 setSections((d.sections || []).map(sec => ({
 key: sec.key,
 title: sec.title,
 items: (sec.items || []).map(it => ({
 _id: it._id,
 sNo: it.sNo,
 item: it.item,
 status: it.status || "Pending",
 remarks: it.remarks || "",
 ncrRaised: it.ncrRaised || false,
 })),
 })));
 })
 .catch(() => toast.error("Failed to load checklist"))
 .finally(() => setFetching(false));
 }, [isEdit, id]);

 /* Item status change */
 const setItemStatus = (secIdx, itemIdx, value) => {
 setSections(prev => {
 const next = prev.map((s, si) =>
 si !== secIdx ? s : {
 ...s,
 items: s.items.map((it, ii) =>
 ii !== itemIdx ? it : { ...it, status: value }
 ),
 }
 );
 return next;
 });
 };

 /* Item remarks change */
 const setItemRemarks = (secIdx, itemIdx, value) => {
 setSections(prev =>
 prev.map((s, si) =>
 si !== secIdx ? s : {
 ...s,
 items: s.items.map((it, ii) =>
 ii !== itemIdx ? it : { ...it, remarks: value }
 ),
 }
 )
 );
 };

 /* NCR toggle */
 const toggleNCR = (secIdx, itemIdx) => {
 setSections(prev =>
 prev.map((s, si) =>
 si !== secIdx ? s : {
 ...s,
 items: s.items.map((it, ii) =>
 ii !== itemIdx ? it : { ...it, ncrRaised: !it.ncrRaised }
 ),
 }
 )
 );
 };

 /* Bulk fill section */
 const fillSection = (secIdx, status) => {
 setSections(prev =>
 prev.map((s, si) =>
 si !== secIdx ? s : {
 ...s,
 items: s.items.map(it => ({ ...it, status })),
 }
 )
 );
 };

 /* Submit */
 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!meta.project) { toast.error("Project is required"); return; }
 if (!meta.inspectionDate){ toast.error("Inspection date is required"); return; }

 const payload = { ...meta, sections };

 setLoading(true);
 try {
 if (isEdit) {
 await updateChecklist(id, payload);
 toast.success("Checklist updated");
 } else {
 await createChecklist(payload);
 toast.success("Checklist saved");
 }
 navigate("/safety", { state: { tab: "checklists" } });
 } catch (err) {
 toast.error(err?.response?.data?.message || "Failed to save");
 } finally {
 setLoading(false);
 }
 };

 /* Excel download */
 const handleDownloadExcel = async () => {
 if (!id) { toast.info("Save the checklist first to download Excel"); return; }
 setDownloading(true);
 try {
 const res = await downloadChecklistExcel(id);
 const url = URL.createObjectURL(new Blob([res.data]));
 const a = document.createElement("a");
 a.href = url;
 a.download = `Safety_Checklist_${id}.xlsx`;
 a.click();
 URL.revokeObjectURL(url);
 } catch (_) {
 toast.error("Excel download failed");
 } finally {
 setDownloading(false);
 }
 };

 if (fetching) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;

 /* JSX */
 return (
 <>
 <PageTitle
 activeMenu={isEdit ? "Edit Safety Checklist" : "New Safety Compliance Checklist"}
 motherMenu="Safety"
 />

 <Form onSubmit={handleSubmit}>

 {/* HEADER META */}
 <Card className="shadow-sm mb-3">
 <Card.Header className="d-flex justify-content-between align-items-center">
 <h6 className="mb-0">
 <i className="fas fa-clipboard-list me-2 text-primary" />
 Daily / Weekly Site Safety Inspection — EHS Compliance Monitoring
 </h6>
 {isEdit && (
 <Button
 variant="outline-success"
 size="sm"
 type="button"
 onClick={handleDownloadExcel}
 disabled={downloading}
 >
 {downloading
 ? <><Spinner size="sm" className="me-1" /> Exporting...</>
 : <><i className="fas fa-file-excel me-1" /> Download Excel</>
 }
 </Button>
 )}
 </Card.Header>
 <Card.Body>
 <Row className="g-3">
 <Col md={4}>
 <Form.Group>
 <Form.Label>Project <span className="text-danger">*</span></Form.Label>
 <Form.Select
 value={meta.project}
 onChange={e => setMeta(p => ({ ...p, project: e.target.value }))}
 required
 >
 <option value="">— Select Project —</option>
 {projects.map(p => (
 <option key={p._id} value={p._id}>{p.projectName || p.name}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Work Order</Form.Label>
 <Form.Select
 value={meta.workOrder}
 onChange={e => setMeta(p => ({ ...p, workOrder: e.target.value }))}
 >
 <option value="">— Select WO —</option>
 {workOrders.map(wo => (
 <option key={wo._id} value={wo._id}>{wo.woNo}{wo.label ? ` – ${wo.label}` : ""}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label>Contract No.</Form.Label>
 <Form.Control
 value={meta.contractNo}
 onChange={e => setMeta(p => ({ ...p, contractNo: e.target.value }))}
 placeholder="e.g. CNSC-2024-001"
 />
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Client / PMC</Form.Label>
 <Form.Control
 value={meta.clientPmc}
 onChange={e => setMeta(p => ({ ...p, clientPmc: e.target.value }))}
 placeholder="Client or PMC name"
 />
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label>Inspection Type</Form.Label>
 <Form.Select
 value={meta.inspectionType}
 onChange={e => setMeta(p => ({ ...p, inspectionType: e.target.value }))}
 >
 {["Daily", "Weekly", "Monthly", "Ad-hoc"].map(t => (
 <option key={t} value={t}>{t}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label>Inspection Date <span className="text-danger">*</span></Form.Label>
 <Form.Control
 type="date"
 value={meta.inspectionDate}
 onChange={e => setMeta(p => ({ ...p, inspectionDate: e.target.value }))}
 required
 />
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label>Next Inspection Due</Form.Label>
 <Form.Control
 type="date"
 value={meta.nextInspectionDue}
 onChange={e => setMeta(p => ({ ...p, nextInspectionDue: e.target.value }))}
 />
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Inspection Conducted By</Form.Label>
 <Form.Select
 value={meta.inspectedBy}
 onChange={e => setMeta(p => ({ ...p, inspectedBy: e.target.value }))}
 >
 <option value="">— Select User —</option>
 {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label>Reviewed / Approved By (PMC)</Form.Label>
 <Form.Select
 value={meta.reviewedBy}
 onChange={e => setMeta(p => ({ ...p, reviewedBy: e.target.value }))}
 >
 <option value="">— Select User —</option>
 {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label>Status</Form.Label>
 <Form.Select
 value={meta.status}
 onChange={e => setMeta(p => ({ ...p, status: e.target.value }))}
 >
 {["Draft", "Submitted", "Reviewed", "Approved", "Closed"].map(s => (
 <option key={s} value={s}>{s}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={12}>
 <Form.Group>
 <Form.Label>Remarks</Form.Label>
 <Form.Control
 as="textarea" rows={2}
 value={meta.remarks}
 onChange={e => setMeta(p => ({ ...p, remarks: e.target.value }))}
 placeholder="Any general observations..."
 />
 </Form.Group>
 </Col>
 </Row>
 </Card.Body>
 </Card>

 {/* LIVE SCORE PANEL */}
 {sections.length > 0 && (
 <Card className="shadow-sm mb-3 border-0"
 style={{ background: "linear-gradient(135deg, #1F3864 0%, #2E75B6 100%)" }}>
 <Card.Body className="py-3">
 <Row className="align-items-center g-3">
 <Col md={4}>
 <div className="text-white">
 <div className="fw-bold mb-1" style={{ fontSize: "0.8rem", opacity: 0.85, letterSpacing: 1 }}>
 COMPLIANCE SCORE
 </div>
 <div className="d-flex align-items-baseline gap-2">
 <span style={{ fontSize: "2.5rem", fontWeight: 700 }}>{score.score}%</span>
 <Badge bg={OVERALL_COLORS[score.overallStatus]} style={{ fontSize: "0.8rem" }}>
 {score.overallStatus}
 </Badge>
 </div>
 <ProgressBar
 now={score.score}
 variant={OVERALL_COLORS[score.overallStatus]}
 style={{ height: 6, borderRadius: 4, marginTop: 6 }}
 />
 </div>
 </Col>
 {[
 { label: "Total Items", value: score.total, icon: "fa-list", color: "#fff" },
 { label: "Compliant", value: score.compliant, icon: "fa-check-circle", color: "#90EE90" },
 { label: "Non-Compliant", value: score.nonCompliant, icon: "fa-times-circle", color: "#FF6B6B" },
 { label: "Partial", value: score.partial, icon: "fa-adjust", color: "#FFD700" },
 { label: "N/A", value: score.na, icon: "fa-minus-circle", color: "#B0B0B0" },
 ].map(s => (
 <Col key={s.label} className="text-center text-white">
 <i className={`fas ${s.icon} mb-1`} style={{ color: s.color, fontSize: 20 }} />
 <div style={{ fontSize: "1.4rem", fontWeight: 700 }}>{s.value}</div>
 <div style={{ fontSize: "0.7rem", opacity: 0.8 }}>{s.label}</div>
 </Col>
 ))}
 </Row>
 </Card.Body>
 </Card>
 )}

 {/* CHECKLIST SECTIONS A–H */}
 {sections.length === 0 ? (
 <div className="text-center py-5">
 <Spinner animation="border" variant="primary" />
 <p className="text-muted mt-2">Loading checklist template…</p>
 </div>
 ) : (
 sections.map((sec, secIdx) => {
 const secScore = computeScore([sec]);
 return (
 <Card key={sec.key} className="shadow-sm mb-3">
 <Card.Header
 className="d-flex justify-content-between align-items-center flex-wrap gap-2"
 style={{ background: "#1F3864" }}
 >
 <h6 className="mb-0 text-white fw-bold">
 <span
 className="badge me-2"
 style={{ background: "#2E75B6", fontSize: "0.85rem", minWidth: 28 }}
 >
 {sec.key}
 </span>
 {sec.title}
 </h6>
 <div className="d-flex align-items-center gap-2 flex-wrap">
 {/* Section score */}
 <Badge bg={OVERALL_COLORS[secScore.overallStatus]} style={{ fontSize: "0.75rem" }}>
 {secScore.score}% — {secScore.overallStatus}
 </Badge>
 {/* Bulk fill */}
 <span className="text-white" style={{ fontSize: "0.75rem", opacity: 0.8 }}>Fill all:</span>
 {["Compliant", "Non-Compliant", "Partial", "N/A"].map(s => (
 <Button
 key={s}
 size="sm"
 variant={s === "Compliant" ? "success" : s === "Non-Compliant" ? "danger" : s === "Partial" ? "warning" : "secondary"}
 style={{ fontSize: "0.7rem", padding: "2px 8px" }}
 type="button"
 onClick={() => fillSection(secIdx, s)}
 >
 {s === "Compliant" ? "" : s === "Non-Compliant" ? "" : s === "Partial" ? "" : "N/A"}
 </Button>
 ))}
 </div>
 </Card.Header>
 <Card.Body className="p-0">
 <div className="table-responsive">
 <Table hover size="sm" className="mb-0 align-middle">
 <thead>
 <tr style={{ background: "#2E75B6" }}>
 <th className="text-white text-center" style={{ width: 50 }}>S.No.</th>
 <th className="text-white">Checklist Item</th>
 <th className="text-white text-center" style={{ width: 130 }}>Compliant</th>
 <th className="text-white text-center" style={{ width: 140 }}>Non-Compliant</th>
 <th className="text-white text-center" style={{ width: 110 }}>Partial</th>
 <th className="text-white text-center" style={{ width: 80 }}>N/A</th>
 <th className="text-white" style={{ width: 220 }}>Remarks / Action</th>
 <th className="text-white text-center" style={{ width: 70 }}>NCR</th>
 </tr>
 </thead>
 <tbody>
 {sec.items.map((item, itemIdx) => {
 const rowBg = itemIdx % 2 === 0 ? "#fff" : "#F0F4FB";
 return (
 <tr key={item._id || itemIdx} style={{ background: rowBg }}>
 {/* S.No. */}
 <td className="text-center text-muted fw-semibold" style={{ fontSize: "0.8rem" }}>
 {item.sNo || itemIdx + 1}
 </td>
 {/* Item description */}
 <td style={{ fontSize: "0.85rem", color: "#1F3864", fontWeight: 500 }}>
 {item.item}
 </td>
 {/* Compliant radio */}
 <td className="text-center">
 <Form.Check
 type="radio"
 name={`status-${secIdx}-${itemIdx}`}
 checked={item.status === "Compliant"}
 onChange={() => setItemStatus(secIdx, itemIdx, "Compliant")}
 className="d-inline-block"
 style={{ accentColor: "#28a745" }}
 />
 {item.status === "Compliant" && (
 <span className="ms-1 text-success fw-bold" style={{ fontSize: "1rem" }}></span>
 )}
 </td>
 {/* Non-Compliant radio */}
 <td className="text-center">
 <Form.Check
 type="radio"
 name={`status-${secIdx}-${itemIdx}`}
 checked={item.status === "Non-Compliant"}
 onChange={() => setItemStatus(secIdx, itemIdx, "Non-Compliant")}
 className="d-inline-block"
 style={{ accentColor: "#dc3545" }}
 />
 {item.status === "Non-Compliant" && (
 <span className="ms-1 text-danger fw-bold" style={{ fontSize: "1rem" }}></span>
 )}
 </td>
 {/* Partial radio */}
 <td className="text-center">
 <Form.Check
 type="radio"
 name={`status-${secIdx}-${itemIdx}`}
 checked={item.status === "Partial"}
 onChange={() => setItemStatus(secIdx, itemIdx, "Partial")}
 className="d-inline-block"
 style={{ accentColor: "#ffc107" }}
 />
 {item.status === "Partial" && (
 <span className="ms-1 text-warning fw-bold" style={{ fontSize: "1rem" }}></span>
 )}
 </td>
 {/* N/A radio */}
 <td className="text-center">
 <Form.Check
 type="radio"
 name={`status-${secIdx}-${itemIdx}`}
 checked={item.status === "N/A"}
 onChange={() => setItemStatus(secIdx, itemIdx, "N/A")}
 className="d-inline-block"
 />
 {item.status === "N/A" && (
 <span className="ms-1 text-muted" style={{ fontSize: "0.75rem" }}>N/A</span>
 )}
 </td>
 {/* Remarks */}
 <td>
 <Form.Control
 size="sm"
 value={item.remarks}
 onChange={e => setItemRemarks(secIdx, itemIdx, e.target.value)}
 placeholder={item.status === "Non-Compliant" ? "Required — describe NCR" : "Optional"}
 style={{
 fontSize: "0.78rem",
 borderColor: item.status === "Non-Compliant" && !item.remarks ? "#dc3545" : undefined,
 }}
 />
 </td>
 {/* NCR toggle */}
 <td className="text-center">
 {item.status === "Non-Compliant" ? (
 <Form.Check
 type="switch"
 checked={item.ncrRaised}
 onChange={() => toggleNCR(secIdx, itemIdx)}
 title="Raise NCR"
 style={{ transform: "scale(0.85)" }}
 />
 ) : (
 <span className="text-muted">—</span>
 )}
 </td>
 </tr>
 );
 })}
 </tbody>
 </Table>
 </div>
 </Card.Body>
 </Card>
 );
 })
 )}

 {/* INSPECTION SUMMARY (read-only preview) */}
 {sections.length > 0 && (
 <Card className="shadow-sm mb-3">
 <Card.Header style={{ background: "#1F3864" }}>
 <h6 className="text-white mb-0 fw-bold">
 <i className="fas fa-chart-bar me-2" />
 Inspection Summary &amp; Score
 </h6>
 </Card.Header>
 <Card.Body>
 <Row className="g-3">
 {[
 { label: "Total Items Inspected", value: score.total },
 { label: "Compliant ()", value: score.compliant, className: "text-success fw-bold" },
 { label: "Non-Compliant ()", value: score.nonCompliant, className: "text-danger fw-bold" },
 { label: "Partial ()", value: score.partial, className: "text-warning fw-bold" },
 { label: "N/A", value: score.na, className: "text-muted" },
 ].map(s => (
 <Col key={s.label} md={2} sm={4} xs={6}>
 <div className="border rounded p-2 text-center h-100">
 <div className="text-muted" style={{ fontSize: "0.72rem" }}>{s.label}</div>
 <div className={`h4 mb-0 fw-bold ${s.className || ""}`}>{s.value}</div>
 </div>
 </Col>
 ))}
 <Col md={2} sm={4} xs={6}>
 <div className="border rounded p-2 text-center h-100" style={{ background: "#DAE3F3" }}>
 <div className="text-muted" style={{ fontSize: "0.72rem" }}>Compliance Score</div>
 <div className="h4 mb-0 fw-bold text-primary">{score.score}%</div>
 </div>
 </Col>
 <Col md={12}>
 <div className="d-flex align-items-center gap-3">
 <span className="fw-semibold">Overall Status:</span>
 <Badge bg={OVERALL_COLORS[score.overallStatus]} style={{ fontSize: "1rem", padding: "0.4rem 1rem" }}>
 {score.overallStatus === "Safe" ? " SAFE"
 : score.overallStatus === "At Risk" ? " AT RISK"
 : score.overallStatus === "Unsafe" ? " UNSAFE"
 : "Pending"}
 </Badge>
 <ProgressBar
 now={score.score}
 variant={OVERALL_COLORS[score.overallStatus]}
 label={`${score.score}%`}
 style={{ flex: 1, height: 18, borderRadius: 8 }}
 />
 </div>
 </Col>
 </Row>
 </Card.Body>
 </Card>
 )}

 {/* SUBMIT BUTTONS */}
 <div className="d-flex gap-2 mb-5">
 <Button type="submit" variant="primary" disabled={loading}>
 {loading ? (
 <><Spinner size="sm" className="me-2" />{isEdit ? "Updating…" : "Saving…"}</>
 ) : isEdit ? "Update Checklist" : "Submit Checklist"}
 </Button>
 {isEdit && (
 <Button
 variant="outline-success"
 type="button"
 onClick={handleDownloadExcel}
 disabled={downloading}
 >
 {downloading
 ? <><Spinner size="sm" className="me-1" />Exporting…</>
 : <><i className="fas fa-file-excel me-1" />Download Excel</>}
 </Button>
 )}
 <Button
 variant="outline-secondary"
 type="button"
 onClick={() => navigate("/safety", { state: { tab: "checklists" } })}
 >
 Cancel
 </Button>
 </div>
 </Form>
 </>
 );
};

export default SafetyChecklistForm;
